import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MAIN_SITE_URL } from '@/utils/constants';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export interface ArticleContent {
  slug: string;
  category: string;
  title: string;
  description: string;
  readTime: string;
  image: string;
  imageAlt: string;
  sections: ArticleSection[];
  faqs: { q: string; a: string }[];
  relatedLinks: { label: string; to: string; external?: boolean }[];
}

interface ArticleSection {
  heading: string;
  content: string[];
  list?: { title?: string; desc: string }[];
  listType?: 'ol' | 'ul' | 'cards' | 'category';
}

export default function ArticleLayout({ article }: { article: ArticleContent }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [article.slug]);

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-16">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">Home</Link>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </span>
            <span className="text-foreground-800 font-medium whitespace-nowrap">{article.title}</span>
          </nav>

          <header className="mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
              {article.category}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
              {article.description}
            </p>
            <div className="flex items-center gap-4 mt-6 text-xs text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-calendar-line text-xs"></i>
                </span>
                Updated: August 2026
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-time-line text-xs"></i>
                </span>
                {article.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-user-line text-xs"></i>
                </span>
                By Sugargoo Spreadsheet Team
              </span>
            </div>
          </header>

          <div className="relative w-full h-64 md:h-96 rounded-lg overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.imageAlt}
              className="w-full h-full object-cover object-center"
            />
          </div>

          <div className="prose-custom">
            {article.sections.map((section, si) => (
              <section key={si} className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  {section.heading}
                </h2>
                {section.content.map((p, pi) => (
                  <p key={pi} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4"
                     dangerouslySetInnerHTML={{ __html: p }}
                  />
                ))}

                {section.list && section.listType === 'ol' && (
                  <ol className="list-decimal pl-5 space-y-3 mt-4">
                    {section.list.map((item, li) => (
                      <li key={li} className="text-sm md:text-base text-foreground-700 leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: item.desc }}
                      />
                    ))}
                  </ol>
                )}

                {section.list && section.listType === 'ul' && (
                  <ul className="list-disc pl-5 space-y-3 mt-4">
                    {section.list.map((item, li) => (
                      <li key={li} className="text-sm md:text-base text-foreground-700 leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: item.desc }}
                      />
                    ))}
                  </ul>
                )}

                {section.list && section.listType === 'cards' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {section.list.map((item, li) => (
                      <div key={li} className="p-4 rounded-lg bg-accent-50 border border-accent-200/60">
                        {item.title && <h4 className="text-sm font-semibold text-foreground-900 mb-1">{item.title}</h4>}
                        <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                )}

                {section.list && section.listType === 'category' && (
                  <div className="space-y-5 mt-6">
                    {section.list.map((item, li) => (
                      <div key={li} className="p-4 rounded-lg bg-background-100/70">
                        {item.title && <h3 className="text-base font-semibold text-foreground-900 mb-1">{item.title}</h3>}
                        <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ))}

            {article.faqs.length > 0 && (
              <section className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4 mt-4">
                  {article.faqs.map((faq, fi) => (
                    <details key={fi} className="group rounded-lg bg-background-100/70 overflow-hidden">
                      <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-background-100 transition-colors">
                        <h3 className="text-sm md:text-base font-semibold text-foreground-900 pr-4">{faq.q}</h3>
                        <span className="w-5 h-5 flex items-center justify-center shrink-0 group-open:rotate-180 transition-transform duration-200">
                          <i className="ri-arrow-down-s-line text-base text-foreground-500"></i>
                        </span>
                      </summary>
                      <div className="px-4 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            )}

            <section className="relative rounded-lg overflow-hidden mb-10">
              <div className="absolute inset-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20inviting%20shopping%20scene%20with%20carefully%20arranged%20fashion%20products%20including%20clothing%20accessories%20and%20lifestyle%20items%20on%20a%20soft%20cream%20colored%20wool%20blanket%2C%20gentle%20golden%20hour%20light%20creating%20a%20peaceful%20dreamy%20atmosphere%2C%20minimalist%20aesthetic%20with%20organic%20textures%20and%20dried%20botanicals%2C%20modern%20lifestyle%20photography%20style%20with%20warm%20neutral%20tones%20and%20subtle%20bokeh&width=1000&height=400&seq=article-cta-v2&orientation=landscape"
                  alt="Explore Sugargoo Spreadsheet resources and product guides"
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-black/25"></div>
              </div>
              <div className="relative z-10 p-8 md:p-12 text-center">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-3">
                  Ready to Start Exploring?
                </h2>
                <p className="text-sm text-background-50/80 mb-6 max-w-lg mx-auto">
                  Browse all product categories on the Sugargoo Spreadsheet or visit the main website to start shopping today.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Back to Homepage
                  </Link>
                  <a
                    href={MAIN_SITE_URL}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-background-50/15 backdrop-blur-sm text-background-50 border border-background-50/25 text-sm font-semibold hover:bg-background-50/25 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Visit Sugargoo Store
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-arrow-right-up-line text-xs"></i>
                    </span>
                  </a>
                </div>
              </div>
            </section>

            <section className="border-t border-background-200/70 pt-8">
              <h3 className="text-sm font-semibold text-foreground-800 mb-4">Related Resources</h3>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Homepage
                </Link>
                {article.relatedLinks.map((link, ri) => (
                  link.external ? (
                    <a
                      key={ri}
                      href={link.to}
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      key={ri}
                      to={link.to}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {link.label}
                    </Link>
                  )
                ))}
              </div>
            </section>
          </div>
        </article>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@graph': [
                {
                  '@type': 'BlogPosting',
                  'headline': article.title,
                  'description': article.description,
                  'image': article.image,
                  'datePublished': '2026-08-01',
                  'dateModified': '2026-08-04',
                  'author': {
                    '@type': 'Organization',
                    'name': 'Sugargoo Spreadsheet',
                    'url': 'https://sugargoospread.site/',
                  },
                  'publisher': {
                    '@type': 'Organization',
                    'name': 'Sugargoo Spreadsheet',
                    'url': 'https://sugargoospread.site/',
                  },
                  'mainEntityOfPage': {
                    '@type': 'WebPage',
                    '@id': 'https://sugargoospread.site' + article.slug,
                  },
                },
                {
                  '@type': 'FAQPage',
                  'mainEntity': article.faqs.map((faq) => ({
                    '@type': 'Question',
                    'name': faq.q,
                    'acceptedAnswer': { '@type': 'Answer', 'text': faq.a },
                  })),
                },
              ],
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}